import {
  toDefaultNumber,
  DateOnlyManipulateType,
  DateOnly
} from '@nx-superprep/utils'

import { Card } from '../../back-end/card'
import { FrontendCardTemplate } from './card-template.model'
import { Constants, StudyPhase, MER } from '../../back-end'
import { StudySetting } from '@longtail-education/superprepstudy-data-model-frontend'
import { SpacedRepModuleConfig } from '@longtail-education/superprep-data-model-frontend'

/**
 * Represents a card in the web application.
 */
export class FrontendCard extends Card<FrontendCardTemplate> {
  static compareById(a: FrontendCard, b: FrontendCard): number {
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  }
  static isEqualId(a: FrontendCard, b: FrontendCard): boolean {
    return a.id === b.id
  }
  // Gameplaay implementation
  gameplayStatus = 'angular'

  // Accessors for template struct
  get sibling() { return this.template.sibling }
  get element() { return this.template.element }
  get moduleId() { return this.template.moduleId }
  get gameplay() { return this.template.gameplay }
  get answer() { return this.template.answer }
  get question() { return this.template.question }
  get choices() { return this.template.choices }
  get choiceSet() { return this.template.choiceSet }
  get shouldSkipReview() { return this.template.shouldSkipReview }

  // Accessors for stats struct
  get score() { return this.stats.score }
  set score(value) { this.stats.score = value }
  get perfectScore() { return this.stats.perfectScore }
  set perfectScore(value) { this.stats.perfectScore = value }
  get difficulty() { return this.stats.difficulty }
  set difficulty(value) { this.stats.difficulty = value }
  get answerTimes() { return this.stats.answerTimes }
  set answerTimes(value) { this.stats.answerTimes = value }
  get reviewDueDate() { return this.stats.reviewDueDate }
  set reviewDueDate(value) { this.stats.reviewDueDate = value }
  get reviewAnswerChance() { return this.stats.reviewAnswerChance }
  set reviewAnswerChance(value) { this.stats.reviewAnswerChance = value }
  get lastReviewDuration() { return this.stats.lastReviewDuration }
  set lastReviewDuration(value) { this.stats.lastReviewDuration = value }
  get lastStudyDate() { return this.stats.lastStudyDate }
  set lastStudyDate(value) { this.stats.lastStudyDate = value }
  get returnToAcquisitionTimes() { return this.stats.returnToAcquisitionTimes }
  set returnToAcquisitionTimes(value) { this.stats.returnToAcquisitionTimes = value }
  get maturityPoints() { return this.stats.maturityPoints }
  set maturityPoints(value) { this.stats.maturityPoints = value }
  get firstChanceAttemptTimes() { return this.stats.firstChanceAttemptTimes }
  set firstChanceAttemptTimes(value) { this.stats.firstChanceAttemptTimes = value }
  get secondChanceAttemptTimes() { return this.stats.secondChanceAttemptTimes }
  set secondChanceAttemptTimes(value) { this.stats.secondChanceAttemptTimes = value }
  get thirdChanceAttemptTimes() { return this.stats.thirdChanceAttemptTimes }
  set thirdChanceAttemptTimes(value) { this.stats.thirdChanceAttemptTimes = value }

  justAcquired = false
  supplement = 0

  // Indicates if the card is in the acquisition phase.
  get inAcquisition(): boolean {
    return this.score < 1.0
  }

  // Indicates if the card is in the review phase.
  get inReview(): boolean {
    return !this.inAcquisition
  }

  // Indicates if the card is due for review today.
  get inReviewDueToday(): boolean {
    return this.inReview && this.isReviewDueToday
  }

  // Checks if the card is due for review today.
  get isReviewDueToday(): boolean {
    return new DateOnly(this.reviewDueDate).isBefore(DateOnly.tomorrow)
  }

  // Indicates if the card is reviewable.
  get isReviewable() {
    return (
      this.inReview &&
      Number(this.supplement) !== 1 &&
      this.maturityPoints <= FrontendCard.MATURITY_MAX_REVIEWABLE
    )
  }

  // Indicates if it's the first presence in the acquisition phase.
  get isFirstAcquisitionPresence(): boolean {
    return this.inAcquisition && this.answerTimes === 0 && this.score === 0
  }

  // Indicates if it's the first presence in the review phase.
  get isFirstReviewPresence(): boolean {
    return this.inReview && this.reviewAnswerChance === 3
  }

  // Indicates if it's the first presence in either phase.
  get isFirstPresence(): boolean {
    return this.isFirstAcquisitionPresence || this.isFirstReviewPresence
  }

  // Checks if a card is complimentary.
  isComplimentary(phase: StudyPhase, isCorrect: boolean) {
    return phase === StudyPhase.Acquisition && isCorrect && this.reviewDueDate
  }

  // Indicates if the card is new.
  get isNew(): boolean {
    return this.isFirstAcquisitionPresence
  }

  // Indicates if the card is in the learning phase.
  get isLearning(): boolean {
    return 1 <= this.maturityPoints && this.maturityPoints < FrontendCard.MATURITY_MEMORIZED_LOWER_BOUND
  }

  // Indicates if the card is memorized.
  get isMemorized(): boolean {
    return FrontendCard.MATURITY_MEMORIZED_LOWER_BOUND <= this.maturityPoints && this.maturityPoints < FrontendCard.MATURITY_MEMORIZED_UPPER_BOUND
  }

  // Indicates if the card is done.
  get isDone(): boolean {
    return FrontendCard.MATURITY_MEMORIZED_UPPER_BOUND <= this.maturityPoints
  }

  // Indicates if it's easy to memorize.
  get easyMemorized(): boolean {
    return this.inReview && this.difficulty < 2
  }

  // Calculates the remaining review chance.
  get remainingReviewChance(): number {
    return this.reviewAnswerChance > 3 ? 3 - this.reviewAnswerChance : 0
  }

  // Indicates if the last study date is today.
  get isLastStudyDateInToday() {
    return new DateOnly(this.lastStudyDate).isToday()
  }

  // Checks if the review is due to a specific date.
  isReviewDueToDate(c: number, d: DateOnlyManipulateType): boolean {
    return new DateOnly(this.reviewDueDate).isSame(new DateOnly().add(c, d))
  }

  // Constants
  static MINIMUM_DAILY_REVIEW_COUNT = Constants.MINIMUM_DAILY_REVIEW_CARD_COUNT
  static MATURITY_BOUND = Constants.MATURITY_POINTS_FOR_DONE_CARD
  static MATURITY_MAX_REVIEWABLE = Constants.MAXIMUM_DAILY_REVIEW_CARD_COUNT
  static MATURITY_MEMORIZED_LOWER_BOUND = 20
  static MATURITY_MEMORIZED_UPPER_BOUND = 80
  static maturityPointsForScore = [0, 2, 4, 6, 8, 10]
  static scoreScale = [0, 0.2, 0.4, 0.6, 0.8, 1.0]
  static maturityPointsForDuration = [15, 25, 37, 51, 67, 85, 100]
  static durationScale = [7, 11, 15, 19, 23, 26, 30]

  // Compares two arrays of cards from the same lesson.
  static compareArrayByDate(a: FrontendCard[] = [], b: FrontendCard[] = [], isAsc = true) {
    const isSameSize = a.length === b.length
    if (!isSameSize) { return (isAsc ? -1 : 1) * (a.length < b.length ? 1 : -1) }
    const result = a.map(c => {
      const found = b.find(r => r.isSameName(c))
      return found ? DateOnly.compare(c.lastStudyDate, found.lastStudyDate) : -1
    })
    return (isAsc ? 1 : -1 ) * ((result.length === a.length && result.reduce((v, acc) => acc + v, 0)) ? 1 : -1)
  }

  // Updates the card when review is done.
  updateWithReviewDone() {
    this.reviewDueDate = undefined
    this.maturityPoints = FrontendCard.MATURITY_BOUND
    this.lastStudyDate = new DateOnly().toTimestamp()
  }

  // Updates the review due date.
  updateReviewDueDate(date: Date) {
    this.reviewDueDate = new DateOnly(date).toTimestamp()
  }

  // Updates the card based on study parameters.
  update({
    phase,
    isCorrect,
    newResponseTime,
    speedyFactor,
    nonSpeedyFactor,
    thirdChanceFactor,
    mer,
  }: {
    phase: StudyPhase
    isCorrect: boolean
    newResponseTime: number
    speedyFactor: number
    nonSpeedyFactor: number
    thirdChanceFactor: number
    mer: number
  }) {
    // update audio duration after loading
    this.template.updateAudioDuration(isCorrect)

    const isSpeedyAnswer: boolean = this.speedyAnswer(isCorrect, newResponseTime, speedyFactor)
    let newScore = this.score
    let newPerfectScore = this.perfectScore
    let newDifficulty = this.difficulty
    let newReviewDueDate: DateOnly|undefined = undefined
    let newReviewAnswerChance = this.reviewAnswerChance
    let newLastReviewDuration = this.lastReviewDuration
    let newReturnToAcquisitionTimes = this.returnToAcquisitionTimes

    this.answerTimes =+ 1

    if (phase === StudyPhase.Acquisition) {
      if (isCorrect) {
        if (this.inReview) { return }
        newScore = getMERScore(newScore, isSpeedyAnswer, mer)
        if (newScore >= 1) {
          this.justAcquired = true
          newReviewDueDate = DateOnly.today.addDays(2)
          newReviewAnswerChance = 3
          newLastReviewDuration = 2
          if (this.shouldSkipReview) {
            newReviewDueDate = undefined
            newLastReviewDuration = FrontendCard.MATURITY_BOUND
            newReviewAnswerChance = 0
          }
        }
      } else {
        if (this.inReview) {
          if (!this.shouldSkipReview) {
            if (newReviewAnswerChance >= 1) {
              newReviewAnswerChance -= 1
              newReviewDueDate = DateOnly.today.addDays(1)
            } else {
              newScore = 0
              newReviewAnswerChance = 0
              newReviewDueDate = undefined
              newLastReviewDuration = 0
              newReturnToAcquisitionTimes += 1
            }
          }
        } else {
          newScore /= 2.0
        }
      }
      if (!this.inReview) {
        newPerfectScore = getMERScore(newPerfectScore, isSpeedyAnswer, mer)
        if (this.returnToAcquisitionTimes === 0) {
          newDifficulty = newPerfectScore - newScore
        }
      }
    } else {
      if (new DateOnly(this.reviewDueDate).isAfter(new DateOnly())) { return }
      let duration = 1.0
      let combinedFactor = nonSpeedyFactor; // 2.2
      switch (newReviewAnswerChance) {
        case 3:
          this.firstChanceAttemptTimes += 1
          if (isSpeedyAnswer) { combinedFactor += speedyFactor }
          break
        case 2:
          this.secondChanceAttemptTimes += 1
          break
        case 1:
          this.thirdChanceAttemptTimes += 1
          combinedFactor *= thirdChanceFactor; // 2.2 * 0.2 = 0.44
          break
        default:
          break
      }
      if (isCorrect) {
        newReviewAnswerChance = 3
        duration = Math.abs(newLastReviewDuration * combinedFactor)
        duration = duration + (duration >= 24 ? Math.floor(Math.random() * 6) - 3 : 0)
        newLastReviewDuration = Math.trunc(duration + Math.ceil(duration < 1 ? 1 : 0))
        newReviewDueDate = DateOnly.today.addDays(newLastReviewDuration)
      } else {
        if (newReviewAnswerChance >= 1) {
          newReviewAnswerChance -= 1
          newReviewDueDate = DateOnly.today.addDays(1)
        } else {
          newScore = 0.0
          newReviewAnswerChance = 0
          newLastReviewDuration = 0
          newReviewDueDate = undefined
          this.returnToAcquisitionTimes += 1
        }
      }
    }
    this.score = newScore
    this.perfectScore = newPerfectScore
    this.difficulty = newDifficulty
    this.reviewDueDate = newReviewDueDate?.toTimestamp()
    this.reviewAnswerChance = newReviewAnswerChance
    this.lastReviewDuration = newLastReviewDuration
    this.lastStudyDate = DateOnly.today.toTimestamp()
    this.returnToAcquisitionTimes = newReturnToAcquisitionTimes
    this.maturityPoints = this.maturity(this.inAcquisition, this.score, this.lastReviewDuration)
  }

  speedyAnswer(isCorrect: boolean, responseTime: number, speedyAnswerTime: number): boolean {
    return (
      isCorrect
      && (this.isFirstAcquisitionPresence || this.inReview)
      && responseTime < speedyAnswerTime
    )
  }

  private maturity(inAcquisition: boolean, score: number, lastReviewDuration: number): number {
    const valueScale = inAcquisition ? FrontendCard.scoreScale : FrontendCard.durationScale
    const maturityScale = inAcquisition
      ? FrontendCard.maturityPointsForScore
      : FrontendCard.maturityPointsForDuration
    const value = inAcquisition ? score : lastReviewDuration
    const index = valueScale.findIndex((v) => v >= value)
    const maturity = index >= 0
      ? maturityScale[index]
      : lastReviewDuration >= FrontendCard.MATURITY_BOUND
        ? FrontendCard.MATURITY_BOUND
        : maturityScale[maturityScale.length - 1]
    return maturity
  }

  isSameName(a: FrontendCard) {
    return a && a.template.id === this.template.id
  }
}

export const getMERScore = (score: number, speedy: boolean, mer: number): number => {
  return speedy ? toDefaultNumber(1.0, 0) : toDefaultNumber(score + ((mer > 0) ? 1.0 / mer : 0), 0)
}

export const getMer = (sibling: string, difficulty: number, cardMER: MER): number => {
  // Validate input parameters
  if (difficulty < 0) {
    console.warn(`Invalid difficulty level: ${difficulty}. Defaulting to 5.`)
    return 5
  }

  // Dynamically construct the MER key based on difficulty
  const merKey = `mer${difficulty}` as keyof MER

  // Validate MER exists and is an array
  const mer = cardMER[merKey]
  if (!Array.isArray(mer) || mer.length === 0) {
    console.warn(`Invalid MER for difficulty ${difficulty}. Returning default value.`)
    return 5
  }

  // Convert sibling to number with robust parsing
  const siblingNum = Number(sibling)

  // Handle non-numeric or invalid sibling input
  if (isNaN(siblingNum)) {
    console.warn(`Non-numeric sibling value: ${sibling}. Returning default value.`)
    return 5
  }

  // Clamp the sibling index to valid array bounds
  const safeIndex = Math.max(1, Math.min(siblingNum, mer.length - 1))

  return mer[safeIndex]
};

export const getRandomIndex = (isCorrect: boolean, setting: StudySetting, config: SpacedRepModuleConfig) => {
  const deckLength = config.deckLength[setting.difficulty];
  const maxBound = isCorrect ? config.rightMax[setting.difficulty] : config.wrongMax[setting.difficulty]
  const minBound = isCorrect ? config.rightMin[setting.difficulty] : config.wrongMin[setting.difficulty]
  // reposition the card in the deck
  const max = Math.min(deckLength, maxBound)
  const min = Math.min(deckLength, minBound)
  return Math.ceil(Math.random() * (max - min) + min)
}

function shuffleInPlace<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1)); [array[i], array[j]] = [array[j], array[i]];
  }
  return array
}

export function shuffleCards(cards: FrontendCard[], phase: StudyPhase = StudyPhase.Acquisition): FrontendCard[] {
  if (!cards.length) return []
  // Step 1: Group cards by `siblingId`
  const groups = new Map<number, FrontendCard[]>()
  for (const card of cards) {
    const sibling = Number(card.template.siblingId) || 0
    if (!groups.has(sibling)) groups.set(sibling, [])
    groups.get(sibling)?.push(card)
  }
  // Step 2: Sort groups by `siblingId`, then shuffle each group
  const sortedShuffledGroups = Array.from(groups.entries())
    .sort(([a], [b]) => a - b)
    .map(([, group]) => shuffleInPlace(group))
  // Step 3: Merge groups
  if (phase !== StudyPhase.Review) {
    return sortedShuffledGroups.flat()
  }
  // StudyPhase.Review: Apply reordering logic
  const result = sortedShuffledGroups[0] || []
  for (let i = 1; i < sortedShuffledGroups.length; i++) {
    const group = sortedShuffledGroups[i]

    if (group.length > 1 && group[0]?.template.isSiblingEqualOrGreater(result[result.length - 1]?.template)) {
      const insertPos = Math.max(0, result.length - 2)
      const lastCard = result.pop()
      if (lastCard) {
        result.splice(insertPos, 0, lastCard)
      }
    }
    result.push(...group)
  }
  return result
}
